import { useUpdateEffect } from 'react-use';

import { TypeaheadSelect } from 'ui/selects';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { isDefined, isString, isNull } from 'utils/tsHelpers';
import { isEmptyString } from 'utils/stringHelpers';

import { useTypeAheadState } from './hooks/useTypeAheadState';
import { ConditionInputButton } from '../buttons';
import { useSearchFieldContext } from '../context/SearchFieldContext';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  useConditionValueErrors,
  useConditionValueController,
  useConditionFieldNameWatch,
} from '../../../hooks/form';
import { useFieldsContext } from '../../../contexts';
import { useToNumberValue } from './hooks/useToNumberValue';
import { useConditionValueInputType } from './hooks/useConditionValueInputType';
import { NumberValue } from '../../../domain';

export const DATA_TEST = {
  input: 'condition-number-input',
};

const getValueAsString = (value: unknown) => `${value}`;
const getDisplayValue = (value: unknown, placeholder: string) =>
  isNull(value) || (isString(value) && isEmptyString(value))
    ? placeholder
    : getValueAsString(value);

export const NumberInput = () => {
  const { t } = useTranslation();
  const fieldName = useConditionFieldNameWatch();
  const error = useConditionValueErrors();
  const {
    field: { value: defaultValue, onChange },
  } = useConditionValueController();
  const { isLoading } = useFieldsContext();
  const { dataType } = useSearchFieldContext();
  const { isFloatInput } = useConditionValueInputType();
  const toNumberValue = useToNumberValue();
  const {
    value,
    isEditing,
    options,
    defaultValues,
    isLoading: isTypeAheadsLoading,
    turnOnEditing,
    turnOffEditing,
    setValue,
  } = useTypeAheadState(
    fieldName,
    defaultValue instanceof NumberValue ? defaultValue.value.toString() : null,
    dataType,
  );

  const placeholder = t('selector.inputPlaceholder');
  const isNumberInputLoading = isLoading || isTypeAheadsLoading;

  useUpdateEffect(() => {
    setValue(null);
  }, [isFloatInput]); // Reset value if switching between ints/floats

  useDebouncedEffect(
    () => {
      onChange({ target: { value: toNumberValue(value) } });
    },
    [value],
    {
      debounceTimeout: 500,
    },
  );

  if (!isEditing) {
    return (
      <ConditionInputButton
        data-test={DATA_TEST.input}
        onClick={turnOnEditing}
        isError={isDefined(error)}
      >
        {getDisplayValue(value, placeholder)}
      </ConditionInputButton>
    );
  }

  return (
    <TypeaheadSelect
      autoFocus
      defaultValues={defaultValues}
      isLoading={isNumberInputLoading}
      options={options}
      onSelect={([option]) => {
        setValue(option);
        turnOffEditing();
      }}
      placeholder={placeholder}
      isError={isDefined(error)}
    />
  );
};
